import { createTheme } from '@mui/material/styles';

/**
 * MUI theme for Page Group Web.
 *
 * Design decisions:
 * - Primary: #1976d2 (MUI default blue) — WCAG AA contrast 4.5:1 on white
 * - Secondary: #FB8C00 (orange) — used for note badges
 * - Background: #F5F5F5 (light grey) — reduces eye strain
 * - Font: Inter (preferred) with Roboto and system sans-serif fallbacks
 * - Focus indicator: 3px solid outline with 3:1 contrast ratio (Req 12.6)
 */
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FB8C00',
      light: '#ffb74d',
      dark: '#e65100',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F5F5F5',
      paper: '#ffffff',
    },
    error: {
      main: '#d32f2f',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#212121',   // contrast ratio ~16:1 on white — WCAG AAA
      secondary: '#616161', // contrast ratio ~5.9:1 on white — WCAG AA
    },
  },

  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
  },

  breakpoints: {
    values: {
      xs: 0,    // < 600px  — 1 column fanpage grid (Req 11.1)
      sm: 600,  // 600–899px — 2 columns (Req 11.2)
      md: 900,  // 900–1199px — 3 columns (Req 11.3)
      lg: 1200, // ≥ 1200px — 4 columns (Req 11.4)
      xl: 1536,
    },
  },

  components: {
    // Ensure no horizontal overflow on any screen size (Req 11.6)
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflowX: 'hidden',
        },
        body: {
          overflowX: 'hidden',
          backgroundColor: '#F5F5F5',
        },
      },
    },

    // Focus indicator: 3px solid outline, contrast ≥ 3:1 (Req 12.6)
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1976d2',
            outlineOffset: '2px',
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '3px solid #1976d2',
            outlineOffset: '2px',
            borderRadius: '2px',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2',
              borderWidth: '2px',
            },
          },
        },
      },
    },

    // Card elevation and border radius
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },

    // Chip/Badge styling
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;
